import type { BodyLook, SkinLook } from "../looks";
import type { PetKit } from "../kit/types";
import type { PetCoatFxSpec, PetFxBurst } from "../kit/petCoatTypes";

/**
 * The six pets' looks (docs/PETS.md "Pets v3", the Names table): each pet's stock model `pet_<pet id>`, its stock coat's
 * name for the Skins sheet, and its three coats — skin id → model `pet_<pet id>__<suffix>` (same rig and parts as the
 * stock pet), with the coat's name, blurb, swatch and the `fallback` tint worn on the stock model until the coat's own
 * model ships. A pet whose own model has not shipped yet is drawn as the Muster Pup (STAND_IN_PET) in `standIn`'s tint
 * (render/skins/wornSkins.ts dressPet), so it reads as a different pet until its art lands.
 */

/** One coat: its skin id (shop.json), model suffix, look and stand-in tint. */
interface CoatDef {
  readonly id: string;
  readonly suffix: string;
  readonly name: string;
  readonly blurb: string;
  readonly swatch: readonly [number, number];
  readonly fallback: BodyLook;
  /** Skins v2: its ambient effect and flourish (views/petCoatFx.ts); its geometry and clip are in its model. */
  readonly fx: PetCoatFxSpec;
}

/** One pet: its id, its stock coat's look, the tint it wears on the pup's model before its own ships, and its coats. */
interface PetLookDef {
  readonly id: string;
  readonly stock: SkinLook;
  readonly standIn: BodyLook | null;
  readonly coats: readonly CoatDef[];
}

/** The pet whose model stands in for one that has not shipped yet. */
export const STAND_IN_PET = "muster_pup";

/** Each coat's flourish clip (art/unitkit/pet_flourish.py) replaces this share of its fidgets. */
const FLOURISH_SHARE = 0.6;
const GOLD = 0xffd36a, GOLD_PALE = 0xfff2c0, SNOW = 0xeef6ff, ICE = 0x9fe0ff, WISP = 0x4a2a6e, VIOLET = 0xb07aff, STAR = 0xfff0b0;

/** A coat's effects: its ambient burst and, `peak` s into its flourish clip, the flourish's burst. */
function coatFx(ambient: PetCoatFxSpec["ambient"], peak: number, burst: PetFxBurst, node?: string): PetCoatFxSpec {
  return { ambient, flourish: { clip: "flourish", share: FLOURISH_SHARE, peak, burst, node } };
}
/** Snow coats: flakes drifting down round it, a flurry shaken off at the flourish. */
const snowFx = (peak: number): PetCoatFxSpec => coatFx({ kind: "snow", colour: SNOW, count: 4, every: [1.2, 2.2], node: "body", radius: 0.6, lift: 1.1, also: { kind: "glints", colour: ICE, count: 1 } }, peak, { kind: "snow", colour: SNOW, count: 10 });
/** Night coats: shadow wisps curling off it with a star glint, stars at the flourish. */
const nightFx = (peak: number, glint: number, burst: PetFxBurst): PetCoatFxSpec => coatFx({ kind: "wisps", colour: WISP, count: 3, every: [1.4, 2.6], node: "body", radius: 0.35, lift: 0.05, also: { kind: "glints", colour: glint, count: 2 } }, peak, burst);

const gilded = (id: string, name: string, blurb: string, peak: number, node = "head", also: PetFxBurst = { kind: "glints", colour: GOLD_PALE, count: 2 }): CoatDef => ({
  id, suffix: "gilded", name, blurb, swatch: [0x9c7420, 0xffe08a],
  fx: coatFx({ kind: "sparkles", colour: GOLD, count: 3, every: [1.6, 3.0], node, radius: 0.3, lift: 0.35, also }, peak, { kind: "sparkles", colour: GOLD, count: 10 }),
  fallback: { tint: 0xe8c25a, mix: 0.62, gain: 1.08, metalness: 0.55, roughness: 0.38, trim: { colour: 0xffcf6a, strength: 0.8 } },
});

export const PET_ROSTER: readonly PetLookDef[] = [
  {
    id: "muster_pup", standIn: null,
    stock: { name: "Muster Coat", blurb: "Ginger fur and a blue bandana: born ready", swatch: [0xc8742a, 0xf6c27a] },
    coats: [
      gilded("pet.pup_gilded", "Gilded Pup", "Gold-leaf fur, a jewelled crown and filigree pauldrons", 0.6),
      { id: "pet.pup_snow", suffix: "snow", name: "Snowdrift", blurb: "A frost ruff, ice-tipped ears and a flurry wherever it trots", swatch: [0x8e9bb0, 0xe8eef6], fx: snowFx(0.9),
        fallback: { tint: 0xf4f1ea, mix: 0.66, gain: 1.12, roughness: 0.85, trim: { colour: 0xbfe6ff, strength: 0.3 } } },
      { id: "pet.pup_midnight", suffix: "midnight", name: "Midnight", blurb: "Star-speckled fur, a crescent moon and shadow wisps", swatch: [0x1d1f2e, 0xd08a3c],
        fx: nightFx(0.95, STAR, { kind: "sparkles", colour: STAR, count: 10 }),
        fallback: { tint: 0x2c2f44, mix: 0.68, gain: 0.85, roughness: 0.5, trim: { colour: 0xc8d4ff, strength: 0.6 } } },
    ],
  },
  {
    id: "lantern_owl", standIn: { tint: 0x9a6a3a, mix: 0.7, gain: 0.95, roughness: 0.8, trim: { colour: 0xffc86a, strength: 0.7 } },
    stock: { name: "Tawny Plumes", blurb: "Round and tawny, amber eyes, a brass lantern on its tail", swatch: [0x8a5a2c, 0xffc86a] },
    coats: [
      gilded("pet.owl_gilded", "Gilded Owl", "Gold-leaf plumes, a crown and a filigree breastplate", 0.55),
      { id: "pet.owl_snow", suffix: "snow", name: "Snowy Owl", blurb: "A frost ruff, icicled wings and its own snowfall", swatch: [0xc8d0dc, 0xf6f8fc], fx: snowFx(0.9),
        fallback: { tint: 0xf4f6fa, mix: 0.7, gain: 1.12, roughness: 0.85, trim: { colour: 0xbfe0ff, strength: 0.5 } } },
      { id: "pet.owl_harvest", suffix: "harvest", name: "Harvest Owl", blurb: "An acorn cap and a collar of autumn leaves", swatch: [0x8a3e1e, 0xf2d6a8],
        fx: coatFx({ kind: "leaves", colour: 0xe8742a, count: 2, every: [1.6, 3], node: "body", radius: 0.45, lift: 0.9, also: { kind: "leaves", colour: 0xc8341e, count: 1 } }, 0.8, { kind: "leaves", colour: 0xf0a030, count: 8 }),
        fallback: { tint: 0xb8642e, mix: 0.68, gain: 1, roughness: 0.75, trim: { colour: 0xffe2b0, strength: 0.4 } } },
    ],
  },
  {
    id: "ember_kit", standIn: { tint: 0xe0622a, mix: 0.7, gain: 1.05, roughness: 0.7, trim: { colour: 0xffa040, strength: 0.8 } },
    stock: { name: "Ember Coat", blurb: "Fox-red fur, soot-dark paws, an ember-tipped tail", swatch: [0xc8501e, 0xffa040] },
    coats: [
      gilded("pet.kit_gilded", "Gilded Kit", "Gold-leaf fur, a jewelled crown, a tail of bright embers", 0.6, "head", { kind: "embers", colour: 0xffa040, count: 2 }),
      { id: "pet.kit_frost", suffix: "frost", name: "Frost Kit", blurb: "Ice-tipped ears, a frost ruff and an ice-blue flame", swatch: [0xc4d4e4, 0x8fd8ff], fx: snowFx(1.05),
        fallback: { tint: 0xf2f6fa, mix: 0.7, gain: 1.12, roughness: 0.8, trim: { colour: 0x8fd8ff, strength: 0.8 } } },
      { id: "pet.kit_shadow", suffix: "shadow", name: "Shadow Kit", blurb: "Starry charcoal fur, a crescent mark and a violet flame", swatch: [0x26242e, 0xb07aff],
        fx: nightFx(1.2, VIOLET, { kind: "wisps", colour: WISP, count: 8 }),
        fallback: { tint: 0x2e2c36, mix: 0.7, gain: 0.85, roughness: 0.6, trim: { colour: 0xb07aff, strength: 0.8 } } },
    ],
  },
  {
    id: "crystal_tortoise", standIn: { tint: 0x5f8a6a, mix: 0.7, gain: 0.95, roughness: 0.75, trim: { colour: 0x7ec8ff, strength: 0.8 } },
    stock: { name: "Crystal Shell", blurb: "A mossy shell sprouting the keep's blue crystal spires", swatch: [0x4a6a4e, 0x7ec8ff] },
    coats: [
      gilded("pet.tortoise_gilded", "Gilded Tortoise", "A crowned tortoise in gold filigree armour", 0.6, "body"),
      { id: "pet.tortoise_amethyst", suffix: "amethyst", name: "Amethyst", blurb: "Glowing amethyst crystals growing from a plum shell", swatch: [0x4a2a5a, 0xc89cff],
        fx: coatFx({ kind: "glints", colour: 0xc89cff, count: 3, every: [1.2, 2.4], node: "glow0", radius: 0.5, lift: 0.2, also: { kind: "motes", colour: 0xe6d0ff, count: 2 } }, 1.15, { kind: "sparkles", colour: 0xc89cff, count: 10 }),
        fallback: { tint: 0x6a3a7e, mix: 0.7, gain: 0.95, roughness: 0.6, trim: { colour: 0xc89cff, strength: 0.8 } } },
      { id: "pet.tortoise_mossback", suffix: "mossback", name: "Mossback", blurb: "A garden on its back: moss, blooms and toadstools", swatch: [0x3e6a2e, 0x9ee07a],
        fx: coatFx({ kind: "motes", colour: 0xccff66, count: 3, every: [1.4, 2.6], node: "body", radius: 0.5, lift: 0.5, also: { kind: "petals", colour: 0xffa0c0, count: 1 } }, 0.9, { kind: "petals", colour: 0xffb0c8, count: 8 }),
        fallback: { tint: 0x5a8a3a, mix: 0.7, gain: 1, roughness: 0.85, trim: { colour: 0x9ee07a, strength: 0.6 } } },
    ],
  },
  {
    id: "bubble_axolotl", standIn: { tint: 0xf2a0b8, mix: 0.72, gain: 1.1, roughness: 0.55, trim: { colour: 0xbff0ff, strength: 0.7 } },
    stock: { name: "Rose Gills", blurb: "Pink with frilly gills and a brass diving collar", swatch: [0xe07a9a, 0xbff0ff] },
    coats: [
      gilded("pet.axolotl_gilded", "Gilded Axolotl", "Gold-leaf skin, a crown and filigree pauldrons", 0.6, "head", { kind: "bubbles", colour: 0xffe08a, count: 2 }),
      { id: "pet.axolotl_midnight", suffix: "midnight", name: "Midnight Axolotl", blurb: "Deep navy, glowing spots, a crescent moon and shadow wisps", swatch: [0x1a2550, 0x5ff0ff],
        fx: nightFx(1.2, 0x5ff0ff, { kind: "sparkles", colour: 0x5ff0ff, count: 10 }),
        fallback: { tint: 0x22306a, mix: 0.72, gain: 0.9, roughness: 0.5, trim: { colour: 0x5ff0ff, strength: 0.9 } } },
      { id: "pet.axolotl_sunny", suffix: "sunny", name: "Sunny Axolotl", blurb: "Lemon yellow with blazing sunburst gills", swatch: [0xe8c83a, 0xff8a7a],
        fx: coatFx({ kind: "sparkles", colour: 0xffc040, count: 3, every: [1.4, 2.6], node: "head", radius: 0.4, lift: 0.3, also: { kind: "motes", colour: 0xff8a7a, count: 2 } }, 0.75, { kind: "glints", colour: 0xffd060, count: 10 }),
        fallback: { tint: 0xf6dc5a, mix: 0.72, gain: 1.1, roughness: 0.55, trim: { colour: 0xff8a7a, strength: 0.6 } } },
    ],
  },
  {
    id: "star_magpie", standIn: { tint: 0x2a2c3a, mix: 0.7, gain: 0.95, roughness: 0.55, trim: { colour: 0xf6f2e8, strength: 0.9 } },
    stock: { name: "Starcrest", blurb: "Black and white, a star-glint crest and a satchel of shiny things", swatch: [0x1e2030, 0xfff2b0] },
    coats: [
      gilded("pet.magpie_gilded", "Gilded Magpie", "Gold-leaf feathers, a crowned star and a breastplate", 0.55),
      { id: "pet.magpie_snow", suffix: "snow", name: "Snow Magpie", blurb: "White and silver, a frost ruff and an icicled tail", swatch: [0xc8ccd6, 0xffe9a8], fx: snowFx(0.9),
        fallback: { tint: 0xeef0f6, mix: 0.7, gain: 1.1, roughness: 0.6, trim: { colour: 0xffe9a8, strength: 0.7 } } },
      { id: "pet.magpie_raven", suffix: "raven", name: "Night Raven", blurb: "Blue-black, a gold crescent, cobalt trim and shadow wisps", swatch: [0x141a2e, 0x3c64d8],
        fx: nightFx(0.5, 0xf0c860, { kind: "sparkles", colour: 0xf0c860, count: 10 }),
        fallback: { tint: 0x1a2038, mix: 0.72, gain: 0.85, metalness: 0.2, roughness: 0.4, trim: { colour: 0x3c64d8, strength: 0.8 } } },
    ],
  },
];

/** A pet's stock model: `pet_<pet id>` (pets.json id). */
export function petModelName(petId: string): string { return `pet_${petId}`; }

/** Every coat as a kit: its pet and its own model. */
export const PET_KIT_LIST: readonly PetKit[] = PET_ROSTER.flatMap((p) => p.coats.map((c) => ({ id: c.id, pet: p.id, model: `${petModelName(p.id)}__${c.suffix}`, fallback: c.fallback, fx: c.fx })));

/** Every coat's look by skin id (the Skins sheet, the catch card's coat name), its tint as `body`. */
export const PET_COAT_LOOKS: Readonly<Record<string, SkinLook>> = Object.fromEntries(
  PET_ROSTER.flatMap((p) => p.coats.map((c) => [c.id, { name: c.name, blurb: c.blurb, swatch: c.swatch, body: c.fallback }] as const)));

const byPet = new Map(PET_ROSTER.map((p) => [p.id, p]));

/** A pet's stock coat look (an unknown pet: the pup's). */
export function petStockLook(petId: string): SkinLook { return (byPet.get(petId) ?? PET_ROSTER[0]).stock; }

/** The tint a pet wears while drawn on the stand-in's model (null for the stand-in itself, or an unknown pet). */
export function petStandIn(petId: string): BodyLook | null { return byPet.get(petId)?.standIn ?? null; }
